import React, { useState, useRef, useEffect, useContext } from 'react';
import { gql, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import {
    Modal,
    Card,
    Fade,
    Grid,
    Autocomplete,
    TextField,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    Tabs,
    Tab
} from '@mui/material';
import Icon from '@mui/material/Icon';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import DataTableCustom from 'components/Tables/DataTableCustom';
import { formatDate, toDateFromParts } from 'utils/formatters';
import EntregaPDF from 'layouts/PDF/compras/entrega';
import CalendarioEntregas from 'layouts/licitaciones/components/CalendarioEntregas';
import pdfIcon from "assets/images/file-type-pdf2.svg";
import { UserContext } from 'context/user';
import ModalRemitos from 'layouts/materiaPrima/components/ModalIngresar/ModalRemitos';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    overflow: 'hidden',
    width: '80vw',
    height: '90vh',
};

const GET_SOLICITUDES_ENTREGA = gql`
  query solicitudesEntregaProveedor($idProveedor: ID, $estadoEmail: String, $limit: Int, $offset: Int) {
    solicitudesEntregaProveedor(idProveedor: $idProveedor, estadoEmail: $estadoEmail, limit: $limit, offset: $offset) {
      solicitudes {
        id
        fechaEntrega
        horaEntregaInicio
        horaEntregaFin
        observaciones
        createdAt
        estadoEmail
        errorEmail
        detalles {
          idProvisionDetalle
          nombre
          cantidad
          unidad
        }
        proveedor {
          id
          razonSocial
          nombre
          apellido
          documento
          user {
            email
          }
        }
        provision {
          id
          estado
          createdAt
          pedidoCompra {
            id
            nombre
            pliegoVencimientoDias
            pliegoParticularidades
            createdUser {
              nombre
              apellido
              telefono
            }
          }
          detalles {
            id
            cantidad
            cantidadEntregada
          }
        }
      }
      total
    }
  }
`;


const GET_PROVEEDORES = gql`
  query getProveedores($filter: JSON, $search: String, $pagination: PaginationInput, $order: Order) {
    paginationInfo {
      pages
      total
    }
    proveedores(
      filter: $filter
      search: $search
      pagination: $pagination
      order: $order
    ) {
      id
      tipoProveedor
      razonSocial
      nombre
      apellido
      tipoDocumento
      documento
      tipoResponsable
      documentosPendientes
      habilitado
      telefono
        telefonoSecundario
        referenciaContacto
      localidad {
        id
        nombre
        provincia {
          id
          nombre
        }
      }
      categorias {
        id
      }
      user {
        email
      }
    }
  }
`;

// Configuración de columnas para DataTableCustom
const formatHourValue = (time) => {
    if (!time) return null;
    const [hours, minutes] = time.split(':').slice(0, 2);
    return `${hours}:${minutes}`;
};

const getScheduleData = (startTime, endTime) => {
    const startHour = formatHourValue(startTime);
    const endHour = formatHourValue(endTime);

    let label = '-';
    if (startHour && endHour) {
        label = startHour === endHour ? `A las ${startHour}` : `Entre las ${startHour} y ${endHour}hs`;
    } else if (startHour || endHour) {
        const hour = startHour || endHour;
        label = `A las ${hour}`;
    }

    const tooltip = (() => {
        if (startHour && endHour) {
            return `Preferentemente entre las ${startHour} y ${endHour}hs`;
        }

        if (startHour || endHour) {
            const hour = startHour || endHour;
            return `Preferentemente a las ${hour}hs`;
        }

        return 'Sin horario especificado';
    })();

    return { startHour, endHour, label, tooltip };
};

const getColumns = (handleGeneratePdf, idProveedorFijo = null) => {
    return [
        {
            field: "estadoEmail",
            headerName: "",
            width: "5%",
            align: "center",
            sortable: true,
            customSortValue: (item) => item.estadoEmail,
            renderCell: (item) => (
                <SoftBox
                    component="div"
                    title={
                        item.estadoEmail === 'success'
                            ? 'Enviado correctamente'
                            : item.estadoEmail === 'failed'
                                ? `Error en el envío: ${item.errorEmail || 'Error desconocido'}`
                                : 'Pendiente'
                    }
                    sx={{
                        cursor: 'help',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Icon
                        sx={{
                            color: item.estadoEmail === 'success'
                                ? '#4CAF50'
                                : item.estadoEmail === 'failed'
                                    ? '#f44336'
                                    : '#ff9800',
                            fontSize: '20px'
                        }}
                    >
                        {item.estadoEmail === 'success'
                            ? 'check_circle'
                            : item.estadoEmail === 'failed'
                                ? 'error'
                                : 'schedule'}
                    </Icon>
                </SoftBox>
            ),
        },
        {
            field: "createdAt",
            headerName: "Fecha Solicitud",
            // width: "10%",
            align: "left",
            sortable: true,
            customSortValue: (item) => new Date(item.createdAt),
            renderCell: (item) => (
                <SoftTypography variant="caption">
                    {formatDate(item.createdAt, 'dd/MM/yyyy hh:mm')}
                </SoftTypography>
            ),
        },
        ...(idProveedorFijo ? [] : [{
            field: "proveedor",
            headerName: "Proveedor",
            // width: "20%",
            align: "left",
            sortable: true,
            customSortValue: (item) => getProveedorName(item.proveedor),
            renderCell: (item) => (
                <SoftTypography variant="caption">
                    {getProveedorName(item.proveedor)}
                </SoftTypography>
            ),
        }]),
        {
            field: "fechaEntrega",
            headerName: "Fecha",
            // width: "12%",
            align: "center",
            sortable: true,
            customSortValue: (item) => {
                const date = toDateFromParts(item.fechaEntrega, item.horaEntregaInicio);
                return date ? date.getTime() : Number.NEGATIVE_INFINITY;
            },
            renderCell: (item) => {
                const { tooltip } = getScheduleData(item.horaEntregaInicio, item.horaEntregaFin);

                return (
                    <SoftTypography
                        variant="caption"
                        title={tooltip}
                        sx={{ cursor: 'help' }}
                    >
                        {item.fechaEntrega ? formatDate(new Date(item.fechaEntrega), 'dd/MM/yyyy') : '-'}
                    </SoftTypography>
                );
            },
        },
        {
            field: "horario",
            headerName: "Horario",
            align: "center",
            sortable: false,
            renderCell: (item) => {
                const { label } = getScheduleData(item.horaEntregaInicio, item.horaEntregaFin);

                return (
                    <SoftTypography variant="caption">
                        {label}
                    </SoftTypography>
                );
            },
        },
        {
            field: "provision",
            headerName: "OP #",
            width: "7%",
            align: "center",
            sortable: true,
            customSortValue: (item) => item.provision?.id || 0,
            renderCell: (item) => (
                <SoftTypography variant="caption">
                    {item.provision?.id || '-'}
                </SoftTypography>
            ),
        },
        {
            field: "detalles",
            headerName: "Productos",
            // width: "28%",
            align: "left",
            sortable: false,
            renderCell: (item) => (
                <SoftBox>
                    {(item.detalles || []).map((detalle, index) => (
                        <SoftTypography variant="caption" key={index} display="block">
                            • {detalle.nombre} - {detalle.cantidad} {detalle.unidad}
                        </SoftTypography>
                    ))}
                </SoftBox>
            ),
        },
        {
            field: "observaciones",
            headerName: "Observaciones",
            // width: "10%",
            align: "left",
            sortable: true,
            renderCell: (item) => (
                <SoftTypography variant="caption" title={item.observaciones || '-'}>
                    {item.observaciones || '-'}
                </SoftTypography>
            ),
        },
        {
            field: "acciones",
            headerName: "PDF",
            width: "5%",
            align: "center",
            sortable: false,
            renderCell: (item) => (
                <SoftBox display="flex" justifyContent="center" alignItems="center">
                    <SoftButton
                        variant="text"
                        color="dark"
                        size="medium"
                        iconOnly
                        circular
                        onClick={() => handleGeneratePdf(item)}
                        title="Generar PDF"
                        sx={{
                            minWidth: 'auto',
                            width: '48px',
                            height: '48px',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <SoftBox
                            component="img"
                            src={pdfIcon}
                            alt="Generar PDF"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    </SoftButton>
                </SoftBox>
            ),
        },
    ];
};


// Función auxiliar para obtener el nombre del proveedor
const getProveedorName = (proveedor) => {
    if (!proveedor) return '';
    return [
        proveedor.razonSocial || proveedor.nombre,
        proveedor.apellido,
        ` - ${proveedor.documento}`
    ].filter(Boolean).join(' ');
};

const getProvisionStatusMeta = (estadoRaw) => {
    const estado = (estadoRaw || '').toLowerCase();

    const mappings = {
        vencida: { color: '#f44336', icon: 'error', label: 'Vencida' },
        anulada: { color: '#212121', icon: 'block', label: 'Anulada' },
        pendiente: { color: '#ff9800', icon: 'schedule', label: 'Pendiente' },
        entregada: { color: '#4caf50', icon: 'check_circle', label: 'Entregada' },
        'entrega parcial': { color: '#2196f3', icon: 'radio_button_checked', label: 'Entrega parcial' },
    };

    if (mappings[estado]) {
        return mappings[estado];
    }

    if (estado.includes('parcial')) {
        return mappings['entrega parcial'];
    }

    return { color: '#9e9e9e', icon: 'help_outline', label: estadoRaw || 'Sin estado' };
};

const GET_PROVISIONES = gql`
  query getProvisiones($filter: JSON, $orderFromToday: Boolean, $pagination: PaginationInput) {
    provisiones(filter: $filter, orderFromToday: $orderFromToday, pagination: $pagination) {
      id
      total
      fechaVencimiento
      anulada
      motivoAnulacion
      enviado
      fechaEnvio
      motivoProrroga
      estado
      pedidoCompra {
        id
        nombre
        tipoCompra
        tipoDetalle
        numeroExpediente
        objetoCompra
		pliegoRequiereDemanda
      }
      proveedor {
        id
        tipoProveedor
        razonSocial
        nombre
        apellido
        documento
      }
      detalles {
        id
        cantidad
        cantidadEntregada
        materiaPrima {
          id
          tipo
          categoria
          nombre
          precio
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
        }
        precioUnitario
        cantidad
        subtotal
        cantidadEntregada
      }
      historial {
        fecha
        evento
        motivo
        user {
          id
          nombre
          apellido
        }
      }
      ingresos {
        id
        tipo
        fechaEntrega
        movimientoDetalles {
          id
          cantidad
          materiaPrima {
            nombre
            categoria
            codigo
          }
          idProvisionDetalle
        }
        pagosProveedor {
          id
          filePathFactura
          estado
          fechaPago
          filePathPagado
        }
      }
      pagos {
        id
        tramite
        estado
        fechaFactura
        tipoFactura
        numeroFactura
        fechaPago
        importe
        retencio_iibb
        retencion_ganancias
        total
      }
    }
  }
`;

export default function ModalHistorialEntregas({ open, handleClose, idProveedorFijo }) {
    const [selectedProveedor, setSelectedProveedor] = useState(null);
    const [selectedEstadoEmail, setSelectedEstadoEmail] = useState('');
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('historial');
    const [search, setSearch] = useState('');
    const [pdfData, setPdfData] = useState(null);
    const [idProvision, setIdProvision] = useState(null);
    const [selectedProvision, setSelectedProvision] = useState();
    const [openModalRemitos, setOpenModalRemitos] = useState(false);
    const modalRef = useRef(null);
    const limit = 20;
    const { user } = useContext(UserContext);

    const { loading: loadingProveedores, data: proveedoresData } = useQuery(GET_PROVEEDORES, {
        variables: {
            search: search || null,
            pagination: {
                page: 1,
                limit: 20,
            },
            order: { field: "razonSocial", order: "ASC" },
            filter: {},
        },
        fetchPolicy: "cache-and-network",
        skip: !open || idProveedorFijo, // Skip cuando hay proveedor fijo
    });

    const { data: proveedorFijoData, loading: loadingProveedorFijo } = useQuery(GET_PROVEEDORES, {
        variables: {
            filter: { id: idProveedorFijo },
            pagination: { page: 1, limit: 1 },
        },
        fetchPolicy: "cache-and-network",
        skip: !open || !idProveedorFijo,
    });

    const { data: dataProvisiones, loading: loadingProvisiones, refetch: refetchProvisiones } = useQuery(GET_PROVISIONES, {
        skip: !open,
        fetchPolicy: 'cache-and-network'
    });

    const { data, loading, refetch } = useQuery(GET_SOLICITUDES_ENTREGA, {
        variables: {
            idProveedor: idProveedorFijo || selectedProveedor?.id,
            estadoEmail: selectedEstadoEmail || null,
            limit,
            offset: (currentPage - 1) * limit
        },
        skip: !open,
        fetchPolicy: 'cache-and-network'
    });

    const solicitudes = data?.solicitudesEntregaProveedor?.solicitudes || [];
    const total = data?.solicitudesEntregaProveedor?.total || 0;

    const handleGeneratePdf = (solicitud) => {
        setPdfData({ ...solicitud, user });
    };

    const handlePdfGenerated = () => {
        setPdfData(null);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(Math.max(1, newPage));
    };

    const handleSearchChange = (event, newInputValue) => {
        setSearch(newInputValue);
    };

    const handleEstadoEmailChange = (event) => {
        setSelectedEstadoEmail(event.target.value);
        setCurrentPage(1);
    };

    const handleProveedorChange = (event, newValue) => {
        setSelectedProveedor(newValue);
        setCurrentPage(1);
    };

    const columns = getColumns(handleGeneratePdf, idProveedorFijo);

    // Efecto para resetear el modal cuando se abre
    useEffect(() => {
        if (open) {
            setActiveTab('historial');
            setSelectedEstadoEmail(''); // Limpiar estado email seleccionado
            setCurrentPage(1); // Resetear página
            if (!idProveedorFijo) {
                setSearch(''); // Limpiar busqueda al abrir el modal solo si no hay proveedor fijo
                setSelectedProveedor(null); // Limpiar proveedor seleccionado solo si no hay proveedor fijo
            }
        }
    }, [open, idProveedorFijo]);



    const handleOpenRemitos = (provision) => {
        if (!provision) {
            return;
        }

        setIdProvision(provision.id);
        setSelectedProvision(provision);
        setOpenModalRemitos(true);
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            aria-labelledby="historial-entregas-modal"
        >
            <Fade in={open}>
                <Card ref={modalRef} sx={style}>
                    <SoftBox
                        p={3}
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'auto'
                        }}
                    >
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <SoftTypography variant="h5">
                                Solicitudes de Entrega
                            </SoftTypography>
                            <Icon sx={{ cursor: 'pointer' }} onClick={handleClose}>
                                close
                            </Icon>
                        </SoftBox>

                        <SoftBox mb={3}>
                            <Tabs
                                value={activeTab}
                                onChange={(event, value) => setActiveTab(value)}
                                textColor="primary"
                                indicatorColor="primary"
                                variant="fullWidth"
                            >
                                <Tab label="Historial" value="historial" />
                                <Tab label="Calendario" value="calendario" />
                            </Tabs>
                        </SoftBox>

                        <SoftBox sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            {activeTab === 'historial' ? (
                                <>
                                    {/* Filtros */}
                                    <Grid container spacing={2} mb={3}>
                                        {!idProveedorFijo && (
                                            <Grid item xs={12} md={5}>
                                                <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                                                    Filtrar por Proveedor
                                                </SoftTypography>
                                                <Autocomplete
                                                    options={proveedoresData?.proveedores || []}
                                                    getOptionLabel={getProveedorName}
                                                    value={selectedProveedor}
                                                    onChange={handleProveedorChange}
                                                    onInputChange={handleSearchChange}
                                                    inputValue={search}
                                                    loading={loadingProveedores}
                                                    filterOptions={(x) => x}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            placeholder="Buscar proveedor..."
                                                            size="small"
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                endAdornment: (
                                                                    <>
                                                                        {loadingProveedores ? <CircularProgress color="inherit" size={20} /> : null}
                                                                        {params.InputProps.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                    noOptionsText={search ? "No se encontraron proveedores" : "Escribe para buscar proveedores"}
                                                />
                                            </Grid>
                                        )}
                                        {idProveedorFijo && (
                                            <Grid item xs={12} md={5}>
                                                <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                                                    Proveedor
                                                </SoftTypography>
                                                <SoftBox
                                                    sx={{
                                                        p: 1.5,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        backgroundColor: '#f5f5f5',
                                                        minHeight: '40px',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <SoftTypography variant="body2">
                                                        {loadingProveedorFijo ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            getProveedorName(proveedorFijoData?.proveedores?.[0] || {})
                                                        )}
                                                    </SoftTypography>
                                                </SoftBox>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} md={idProveedorFijo ? 4 : 4}>
                                            <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                                                Estado de Notificación
                                            </SoftTypography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={selectedEstadoEmail}
                                                    onChange={handleEstadoEmailChange}
                                                    displayEmpty
                                                    variant="outlined"
                                                >
                                                    <MenuItem value="">Todas</MenuItem>
                                                    <MenuItem value="success">Exitosas</MenuItem>
                                                    <MenuItem value="failed">Fallidas</MenuItem>
                                                    <MenuItem value="pending">Pendientes</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3} display="flex" alignItems="end">
                                            <SoftButton
                                                color="info"
                                                onClick={() => {
                                                    if (!idProveedorFijo) {
                                                        setSelectedProveedor(null);
                                                        setSearch('');
                                                    }
                                                    setSelectedEstadoEmail('');
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <Icon>clear_all</Icon>
                                                &nbsp;Limpiar filtros
                                            </SoftButton>
                                        </Grid>
                                    </Grid>

                                    {/* Tabla con DataTableCustom */}
                                    <SoftBox sx={{ px: 2, pb: 2 }}>
                                        <DataTableCustom
                                            columns={columns}
                                            data={solicitudes}
                                            loading={loading}
                                            showActions={false}
                                            emptyMessage="No hay solicitudes de entrega registradas"
                                            loadingMessage="Cargando solicitudes..."
                                            maxHeight="100%"
                                            showFooter={true}
                                            footerContent={
                                                <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                                    <SoftTypography variant="caption">
                                                        Mostrando {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, total)} de {total} solicitudes
                                                    </SoftTypography>
                                                    <SoftBox display="flex" gap={1} alignItems="center">
                                                        <SoftButton
                                                            size="small"
                                                            color="info"
                                                            disabled={currentPage === 1}
                                                            onClick={() => handlePageChange(currentPage - 1)}
                                                        >
                                                            <Icon>chevron_left</Icon>
                                                            Anterior
                                                        </SoftButton>
                                                        <SoftTypography variant="caption" sx={{ mx: 2 }}>
                                                            Página {currentPage} de {Math.ceil(total / limit)}
                                                        </SoftTypography>
                                                        <SoftButton
                                                            size="small"
                                                            color="info"
                                                            disabled={currentPage >= Math.ceil(total / limit)}
                                                            onClick={() => handlePageChange(currentPage + 1)}
                                                        >
                                                            Siguiente
                                                            <Icon>chevron_right</Icon>
                                                        </SoftButton>
                                                    </SoftBox>
                                                </SoftBox>
                                            }
                                            sortable={true}
                                            defaultSort={{ field: "createdAt", direction: "desc" }}
                                            zebraStripes={true}
                                            hover={true}
                                            border={true}
                                        />
                                    </SoftBox>
                                </>
                            ) : (
                                <CalendarioEntregas
                                    idProveedor={null}
                                    solicitudes={solicitudes}
                                    loadingSolicitudes={loading}
                                    provisiones={dataProvisiones?.provisiones || []}
                                    loadingProvisiones={false}
                                    onGeneratePdf={handleGeneratePdf}
                                    onOpenRemitos={handleOpenRemitos}
                                    forceOpenCollapse={true}
                                />
                            )}
                        </SoftBox>



                        {/* Componente PDF invisible para generacion */}
                        {pdfData && (
                            <SoftBox sx={{ display: 'none' }}>
                                <EntregaPDF
                                    data={pdfData}
                                    onGeneratePdf={handlePdfGenerated}
                                />
                            </SoftBox>
                        )}

                    </SoftBox>
                    <ModalRemitos
                        open={openModalRemitos}
                        handleClose={() => setOpenModalRemitos(false)}
                        idProvision={idProvision}
                        provisionData={selectedProvision}
                    />
                </Card>
            </Fade>

        </Modal>
    );
}

ModalHistorialEntregas.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    idProveedorFijo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
